import { useEffect, useMemo, type ReactNode } from "react";
import type { Identifier } from "ra-core";
import { useGetIdentity, useGetList, useGetMany, useTranslate } from "ra-core";
import { Link } from "react-router";

import { useIsMobile } from "@/hooks/use-mobile";

import { LeadAlertCards } from "../alerts/LeadAlertCards";
import { useAdvisorLeadAlerts } from "../alerts/useLeadAlerts";
import { INBOX_STAGE } from "../deals/stages";
import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { ScoreRing } from "../design/ScoreRing";
import { APP_LOCALE } from "../misc/locale";
import { MobileContent } from "../layout/MobileContent";
import type {
  Contact,
  ContactNote,
  Deal,
  DealActivityType,
  Project,
  SalesProject,
  Task,
} from "../types";
import {
  buildHoyPlan,
  dealIdsInHoyPlan,
  type HoyClient,
  type HoyContact,
  type HoyDeal,
  type HoyTask,
} from "./hoyPlan";
import { DashboardStepper } from "./DashboardStepper";
import { HoyPersonCard } from "./HoyPersonCard";
import { MobileHomeHeader } from "./MobileDashboard";
import { UpcomingBirthdays } from "./UpcomingBirthdays";
import { computeUpcomingBirthdays } from "./upcomingBirthdaysWindow";

/**
 * Almikia Epic E7, TASK-089: the asesora's "Hoy" home
 * (`docs/almikia/design/A-movil`, left phone) -- "her list of actions, not
 * a report" (design-system.md section 2). Renders identically on desktop
 * (as `Dashboard`'s replacement) and mobile (as `MobileDashboard`'s
 * replacement), and at its own `/hoy` route (`CRM.tsx`) so a Líder BPO can
 * later open her own day (TASK-093). Always scoped to the signed-in user's
 * OWN records, regardless of her role.
 */

const DISCOVERY_STAGE = "discovery";

// Generous, fixed caps (NFR: bounded to the open items actually needed,
// mirrors `useAdvisorProgress.ts`'s per-query cap convention) -- an
// advisor's own open Prospecto/Discovery leads and undone tasks, plus a
// small recent-done-tasks sample used only to count today's finished
// touches for the ring.
const MAX_DEALS = 500;
const MAX_OPEN_TASKS = 500;
const MAX_RECENT_DONE_TASKS = 50;
const MAX_BIRTHDAY_CLIENTS = 1000;
const MAX_SALES_PROJECTS = 20;

const HEADER_DATE_FORMAT = new Intl.DateTimeFormat(APP_LOCALE, {
  weekday: "long",
  day: "numeric",
  month: "long",
});

/** "Lunes 28" (capitalized weekday, no year -- matches the mockup's "Lunes 28"). */
function formatHeaderDate(now: Date): string {
  const formatted = HEADER_DATE_FORMAT.format(now);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export const AdvisorToday = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const salesId = identity?.id;
  const hasIdentity = Number.isInteger(salesId);
  const now = useMemo(() => new Date(), []);

  // Onboarding stepper gating -- identical to Dashboard.tsx/MobileDashboard.tsx,
  // Hoy never bypasses it just because the signed-in user is an asesora.
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", { pagination: { page: 1, perPage: 1 } });
  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });

  const { data: salesProjects } = useGetList<SalesProject>(
    "sales_projects",
    {
      pagination: { page: 1, perPage: MAX_SALES_PROJECTS },
      sort: { field: "id", order: "ASC" },
      filter: { sales_id: salesId, "ended_at@is": null },
    },
    { enabled: hasIdentity },
  );
  const projectIds = (salesProjects ?? []).map((sp) => sp.project_id);
  const { data: projects } = useGetMany<Project>(
    "projects",
    { ids: projectIds },
    { enabled: projectIds.length > 0 },
  );

  const {
    data: deals,
    isPending: isDealsPending,
    error: dealsError,
    refetch: refetchDeals,
  } = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: MAX_DEALS },
      sort: { field: "created_at", order: "ASC" },
      filter: {
        sales_id: salesId,
        "stage@in": `(${INBOX_STAGE},${DISCOVERY_STAGE})`,
        "archived_at@is": null,
      },
    },
    { enabled: hasIdentity },
  );

  const {
    data: openTasks,
    isPending: isOpenTasksPending,
    error: openTasksError,
    refetch: refetchOpenTasks,
  } = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: MAX_OPEN_TASKS },
      sort: { field: "due_date", order: "ASC" },
      filter: { sales_id: salesId, "done_date@is": null },
    },
    { enabled: hasIdentity },
  );
  const { data: recentDoneTasks, isPending: isDoneTasksPending } =
    useGetList<Task>(
      "tasks",
      {
        pagination: { page: 1, perPage: MAX_RECENT_DONE_TASKS },
        sort: { field: "done_date", order: "DESC" },
        filter: { sales_id: salesId, "done_date@not.is": null },
      },
      { enabled: hasIdentity },
    );

  const {
    data: birthdayClients,
    isPending: isClientsPending,
    error: clientsError,
  } = useGetList<HoyClient>(
    "clients",
    {
      pagination: { page: 1, perPage: MAX_BIRTHDAY_CLIENTS },
      sort: { field: "id", order: "ASC" },
      filter: { sales_id: salesId, "birthday@not.is": null },
    },
    { enabled: hasIdentity },
  );

  const contactIds = useMemo(() => {
    const ids = new Set<Identifier>();
    for (const deal of deals ?? []) {
      if (deal.contact_ids?.[0] != null) ids.add(deal.contact_ids[0]);
    }
    for (const task of openTasks ?? []) {
      if (task.contact_id != null) ids.add(task.contact_id);
    }
    // Almikia Epic EW1 (TASK-125, retry round): a birthday card's shared
    // "Enviar WhatsApp" action always dials `client.phone` (hoyPlan.ts), so
    // this fetch only lends the linked contact's first name -- never its own
    // `phone_jsonb`, which is not used for the birthday case. Scoped to the
    // same today/tomorrow window `buildHoyPlan` renders cards for (not every
    // one of up to `MAX_BIRTHDAY_CLIENTS` open birthdays), so this request
    // stays small and a failure never takes down every card's name/phone.
    const todayOrTomorrowClientIds = new Set(
      computeUpcomingBirthdays(birthdayClients ?? [], now)
        .filter((row) => row.daysUntil <= 1)
        .map((row) => row.id),
    );
    for (const client of birthdayClients ?? []) {
      if (
        todayOrTomorrowClientIds.has(client.id) &&
        client.contact_id != null
      ) {
        ids.add(client.contact_id);
      }
    }
    return Array.from(ids);
  }, [deals, openTasks, birthdayClients, now]);

  const { data: contacts } = useGetMany<Contact>(
    "contacts",
    { ids: contactIds },
    { enabled: contactIds.length > 0 },
  );
  const contactsById = useMemo(() => {
    const map = new Map<Identifier, HoyContact>();
    for (const contact of contacts ?? []) map.set(contact.id, contact);
    return map;
  }, [contacts]);

  // Almikia Epic EA (TASK-112), NFR: at most one batched catalogue read for
  // the next-step task cards' activity name, no per-card request.
  const activityTypeIds = useMemo(() => {
    const ids = new Set<Identifier>();
    for (const task of openTasks ?? []) {
      if (task.activity_type_id != null) ids.add(task.activity_type_id);
    }
    return Array.from(ids);
  }, [openTasks]);
  const { data: activityTypes } = useGetMany<DealActivityType>(
    "deal_activity_types",
    { ids: activityTypeIds },
    { enabled: activityTypeIds.length > 0 },
  );
  const activityTypeNamesById = useMemo(() => {
    const map = new Map<Identifier, string>();
    for (const activityType of activityTypes ?? []) {
      map.set(activityType.id, activityType.name);
    }
    return map;
  }, [activityTypes]);

  const isPending =
    isPendingContact ||
    isPendingContactNotes ||
    isDealsPending ||
    isOpenTasksPending ||
    isDoneTasksPending ||
    isClientsPending;

  const plan = useMemo(
    () =>
      buildHoyPlan({
        deals: (deals ?? []) as HoyDeal[],
        tasks: [...(openTasks ?? []), ...(recentDoneTasks ?? [])] as HoyTask[],
        clients: birthdayClients ?? [],
        contactsById,
        activityTypeNamesById,
        now,
      }),
    [
      deals,
      openTasks,
      recentDoneTasks,
      birthdayClients,
      contactsById,
      activityTypeNamesById,
      now,
    ],
  );

  // Almikia Epic ER (TASK-007): called ONCE here (NFR: one refresh call, not
  // one per surface) -- its cards feed both `LeadAlertCards` below (the
  // leads NOT already shown above) and each `HoyPersonCard` above (the
  // leads that ARE, acceptance criterion 2: "appears once (with its alert
  // reason)", never dropped).
  const {
    cards: alertCards,
    isPending: isAlertsPending,
    error: alertsError,
    refetchAlerts,
    evaluateAndRefresh,
  } = useAdvisorLeadAlerts();

  // Acceptance criterion 3: evaluate on load, then read whatever the engine
  // (or the throttle) leaves in `lead_alerts`.
  useEffect(() => {
    void evaluateAndRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only.
  }, []);

  const excludeDealIds = dealIdsInHoyPlan(plan);
  // One alert per lead already on this list -- the oldest raised (`alerts`
  // in `useAdvisorLeadAlerts` is sorted `raised_at` ascending), matching
  // "its alert reason" (singular) in the acceptance criterion.
  const alertByDealId = useMemo(() => {
    const map = new Map<Identifier, (typeof alertCards)[number]>();
    for (const alertCard of alertCards) {
      if (excludeDealIds.has(alertCard.dealId) && !map.has(alertCard.dealId)) {
        map.set(alertCard.dealId, alertCard);
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `excludeDealIds` is a fresh Set every render (derived from `plan`); `plan` itself is the real dependency.
  }, [alertCards, plan]);
  const otherAlertCards = alertCards.filter(
    (alertCard) => !excludeDealIds.has(alertCard.dealId),
  );

  const firstName = identity?.fullName?.split(" ")[0] ?? "";
  const projectNames = (projects ?? [])
    .map((project) => project.name)
    .join(", ");
  const error = dealsError ?? openTasksError ?? clientsError;
  const totalPlanned = plan.doneTodayCount + plan.pendingTouchCount;

  const planSentence =
    plan.pendingTouchCount === 0
      ? translate("crm.dashboard.hoy.plan_all_done")
      : plan.mostUrgentTouch?.reason.kind === "prospect"
        ? translate("crm.dashboard.hoy.plan_pending_with_hours", {
            smart_count: plan.pendingTouchCount,
            name: plan.mostUrgentTouch.name,
            hours: plan.mostUrgentTouch.reason.hours,
          })
        : translate("crm.dashboard.hoy.plan_pending", {
            smart_count: plan.pendingTouchCount,
            name: plan.mostUrgentTouch?.name ?? "",
          });

  const content = (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-sm text-muted-foreground">
          {formatHeaderDate(now)}
          {projectNames ? ` · ${projectNames}` : ""}
        </p>
        <h1 className="text-2xl font-bold">
          {translate("crm.dashboard.hoy.greeting", { name: firstName })}
        </h1>
        {/* Almikia Epic E7 (TASK-092): "Mi avance" reachable with one tap
            from Hoy, in both shells (this component renders identically on
            desktop and mobile -- see the module docstring above). */}
        <Link
          to="/mi-avance"
          className="mt-2 inline-flex h-11 items-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"
        >
          {translate("crm.dashboard.hoy.my_progress_link")}
        </Link>
      </div>

      <div className="flex items-center gap-4 rounded-2xl border bg-card p-4">
        <ScoreRing
          value={plan.doneTodayCount}
          max={totalPlanned || 1}
          unit={translate("crm.dashboard.hoy.ring_unit", {
            total: totalPlanned,
          })}
          label={translate("crm.dashboard.hoy.ring_label")}
        />
        {/* design-system.md section 3: "frase debajo con la persona como
            sujeto" -- body copy, >= 15px desktop / 16px mobile. */}
        <div className="text-base">
          <p className="mb-1 font-bold">
            {translate("crm.dashboard.hoy.ring_title")}
          </p>
          <p className="text-muted-foreground">{planSentence}</p>
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-xl font-semibold text-muted-foreground">
          {translate("crm.dashboard.hoy.list_title")}
        </h2>
        {error ? (
          <ListErrorState
            message={translate("crm.common.list_error")}
            onRetry={() => {
              refetchDeals();
              refetchOpenTasks();
            }}
          />
        ) : isPending ? (
          <ListCardsSkeleton />
        ) : plan.cards.length === 0 ? (
          <ListEmptyState
            title={translate("crm.dashboard.hoy.empty_title")}
            description={translate("crm.dashboard.hoy.empty_description")}
          />
        ) : (
          <div className="flex flex-col gap-2">
            {plan.cards.map((card) => (
              <HoyPersonCard
                key={card.id}
                card={card}
                translate={translate}
                alert={
                  card.dealId != null
                    ? (alertByDealId.get(card.dealId) ?? null)
                    : null
                }
                onAlertActivityLogged={evaluateAndRefresh}
              />
            ))}
          </div>
        )}
      </div>

      {/* Almikia Epic ER (TASK-007): the advisor's own open lead alerts on
          the OTHER leads -- one already shown as a card above instead shows
          its alert reason there (acceptance criterion 2, `alertByDealId`
          above), never twice. */}
      <LeadAlertCards
        cards={otherAlertCards}
        isPending={isAlertsPending}
        error={alertsError}
        refetchAlerts={refetchAlerts}
        onActivityLogged={evaluateAndRefresh}
      />

      <UpcomingBirthdays />
    </div>
  );

  // Onboarding stepper gating -- identical to Dashboard.tsx/MobileDashboard.tsx,
  // Hoy never bypasses it just because the signed-in user is an asesora.
  // Kept below `content` (not as an early return) so both branches go
  // through the same mobile-chrome wrapping below.
  let body: ReactNode = null;
  if (isPendingContact || isPendingContactNotes) {
    body = null;
  } else if (!totalContact) {
    body = <DashboardStepper step={1} />;
  } else if (!totalContactNotes) {
    body = <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />;
  } else {
    body = content;
  }

  if (isMobile) {
    return (
      <>
        <MobileHomeHeader />
        <MobileContent>{body}</MobileContent>
      </>
    );
  }

  return body;
};
